/**
 * ExportRenderer.jsx
 * Hidden off-screen component that renders charts for export capture.
 * Normal mode: renders only the active-tab chart per section.
 * Master mode (captureAll): renders ALL charts in every section for dev verification.
 * Mounted only when isExporting === true. After charts render,
 * captures them as PNG images via html2canvas and calls onCaptureComplete.
 */

import { useEffect, useRef, useState, memo, Fragment } from 'react';
import { useApp } from '../context/AppContext';
import { captureAllCharts } from '../utils/exportService/chartCapture';

// Chart tab mappings (shared with ChartSelectionModal)
import { OVERVIEW_TABS, PROGRESS_TABS, PARTNERSHIP_TABS, INNOVATION_TABS, PORTFOLIO_OVERVIEW_TABS, PORTFOLIO_IMPACT_AREA_TABS, PORTFOLIO_REGIONAL_TABS, PORTFOLIO_PARTNERSHIPS_TABS } from './charts/chartRegistry';

// Extra charts not in tab mappings
import { AowTrajectoryChart } from './charts/progress/AowTrajectoryChart';
import { AowTocFrameworkDiagram } from './charts/overview/AowTocFrameworkDiagram';
import { AowTocFrameworkDetailDiagram } from './charts/overview/AowTocFrameworkDetailDiagram';
import { PartnershipDataProvider } from './charts/partnership/usePartnershipData.jsx';
import { PortfolioDataProvider } from './charts/portfolio/usePortfolioData';

function renderTabChart(tabs, activeTab, id) {
  const Component = tabs[activeTab];
  if (!Component) return null;
  return (
    <div id={`export-chart-${id}`} style={{ marginBottom: 16 }}>
      <Component />
    </div>
  );
}

// Per-AoW keys are rendered separately in the AoW loop — exclude from regular progress rendering
const PER_AOW_KEYS = ['aowFramework', 'aowFrameworkDetail', 'aowTrajectory'];

function renderAllTabCharts(tabs, sectionPrefix, excludeKeys = []) {
  return Object.entries(tabs)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([key, Component]) => (
      <div key={key} id={`export-chart-${sectionPrefix}-${key}`} style={{ marginBottom: 16 }}>
        <Component />
      </div>
    ));
}

function renderSelectedCharts(tabs, sectionPrefix, selectedKeys, excludeKeys = []) {
  if (!selectedKeys?.length) return null;
  return selectedKeys
    .filter(key => !excludeKeys.includes(key))
    .map(key => {
      const Component = tabs[key];
      if (!Component) return null;
      return (
        <div key={key} id={`export-chart-${sectionPrefix}-${key}`} style={{ marginBottom: 16 }}>
          <Component />
        </div>
      );
    });
}

const ExportRenderer = memo(function ExportRenderer({ onCaptureComplete }) {
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const { sectionTabs, trafficLights, exportRequest, reportMode } = useApp();

  const isPortfolio = reportMode === 'portfolio';
  const masterSection = exportRequest?.masterSection || null;
  const chartSelections = exportRequest?.chartSelections || null;
  const aows = [...(trafficLights?.computed || [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  // Wait for charts to mount and render (maps need TopoJSON fetch)
  // Master mode / multi-chart selection renders more charts, so allow more time
  const hasMultipleSelections = chartSelections && Object.values(chartSelections).reduce((sum, arr) => sum + arr.length, 0) > 3;
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), (masterSection || hasMultipleSelections) ? 4000 : 3000);
    return () => clearTimeout(timer);
  }, [masterSection, hasMultipleSelections]);

  // Capture once ready
  useEffect(() => {
    if (!ready || !containerRef.current) return;

    captureAllCharts(containerRef.current).then(chartImages => {
      onCaptureComplete(chartImages);
    });
  }, [ready, onCaptureComplete]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '800px',
        background: '#ffffff',
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      {isPortfolio ? (
        /* ── Portfolio Narrative mode ── */
        <PortfolioDataProvider>
          {/* Portfolio Overview charts */}
          {chartSelections?.['portfolio-overview']?.length
            ? renderSelectedCharts(PORTFOLIO_OVERVIEW_TABS, 'portfolio-overview', chartSelections['portfolio-overview'])
            : renderSelectedCharts(PORTFOLIO_OVERVIEW_TABS, 'portfolio-overview', ['programBreakdown', 'kpi', 'impactAreas'])}

          {/* Impact Area Focus charts */}
          {chartSelections?.['portfolio-impact-areas']?.length
            ? renderSelectedCharts(PORTFOLIO_IMPACT_AREA_TABS, 'portfolio-impact-areas', chartSelections['portfolio-impact-areas'])
            : renderSelectedCharts(PORTFOLIO_IMPACT_AREA_TABS, 'portfolio-impact-areas', ['iaGender', 'iaClimate', 'iaNutrition', 'iaBiodiversity', 'iaPoverty'])}

          {/* Regional Focus charts */}
          {chartSelections?.['portfolio-regional']?.length
            ? renderSelectedCharts(PORTFOLIO_REGIONAL_TABS, 'portfolio-regional', chartSelections['portfolio-regional'])
            : renderSelectedCharts(PORTFOLIO_REGIONAL_TABS, 'portfolio-regional', ['africaBar', 'americasBar', 'asiaBar'])}

          {/* Portfolio Partnerships charts */}
          <PartnershipDataProvider>
            {chartSelections?.['portfolio-partnerships']?.length
              ? renderSelectedCharts(PORTFOLIO_PARTNERSHIPS_TABS, 'portfolio-partnerships', chartSelections['portfolio-partnerships'])
              : renderSelectedCharts(PORTFOLIO_PARTNERSHIPS_TABS, 'portfolio-partnerships', ['partnerFlow', 'topPartners'])}
          </PartnershipDataProvider>
        </PortfolioDataProvider>
      ) : (
        /* ── Annual Report (Technical) mode ── */
        <>
          {/* Section 1: Overview */}
          {masterSection === 'overview'
            ? renderAllTabCharts(OVERVIEW_TABS, 'overview')
            : !masterSection && (chartSelections?.overview?.length
              ? renderSelectedCharts(OVERVIEW_TABS, 'overview', chartSelections.overview)
              : renderTabChart(OVERVIEW_TABS, sectionTabs.overview, 'overview'))}

          {/* Section 2: Progress (per-AoW keys rendered separately below) */}
          {masterSection === 'progress'
            ? renderAllTabCharts(PROGRESS_TABS, 'progress', PER_AOW_KEYS)
            : !masterSection && (chartSelections?.progress?.length
              ? renderSelectedCharts(PROGRESS_TABS, 'progress', chartSelections.progress, PER_AOW_KEYS)
              : renderTabChart(PROGRESS_TABS, sectionTabs.progress, 'progress'))}

          {/* Per-AoW charts: trajectory, framework, framework detail — respect chart selections */}
          {(!masterSection || masterSection === 'progress') && aows.map(aow => {
            const id = aow.name.replace(/\s+/g, '-').toLowerCase();
            const progressSel = chartSelections?.progress;
            const showTrajectory = masterSection || (progressSel ? progressSel.includes('aowTrajectory') : true);
            const showFramework = masterSection || (progressSel ? progressSel.includes('aowFramework') : sectionTabs.progressAow === 'framework');
            const showDetail = masterSection || (progressSel ? progressSel.includes('aowFrameworkDetail') : sectionTabs.progressAow === 'frameworkDetail');
            return (
              <Fragment key={id}>
                {showTrajectory && (
                  <div id={`export-chart-aowTrajectory-${id}`} style={{ marginBottom: 16 }}>
                    <AowTrajectoryChart indicators={aow.indicators} hlos={aow.hlos} />
                  </div>
                )}
                {showFramework && (
                  <div id={`export-chart-aowFramework-${id}`} style={{ marginBottom: 16 }}>
                    <AowTocFrameworkDiagram aowFilter={id} />
                  </div>
                )}
                {showDetail && (
                  <div id={`export-chart-aowFrameworkDetail-${id}`} style={{ marginBottom: 16 }}>
                    <AowTocFrameworkDetailDiagram aowFilter={id} />
                  </div>
                )}
              </Fragment>
            );
          })}

          {/* Section 3: Partnerships */}
          {(!masterSection || masterSection === 'partnerships') && (
            <PartnershipDataProvider>
              {masterSection === 'partnerships'
                ? renderAllTabCharts(PARTNERSHIP_TABS, 'partnerships')
                : chartSelections?.partnerships?.length
                  ? renderSelectedCharts(PARTNERSHIP_TABS, 'partnerships', chartSelections.partnerships)
                  : renderTabChart(PARTNERSHIP_TABS, sectionTabs.partnerships, 'partnerships')}
            </PartnershipDataProvider>
          )}

          {/* Innovation Analytics — selected charts or default 3 (skip in master mode) */}
          {!masterSection && (
            chartSelections?.innovation?.length
              ? renderSelectedCharts(INNOVATION_TABS, 'innovation', chartSelections.innovation)
              : renderSelectedCharts(INNOVATION_TABS, 'innovation', ['type', 'useLevel', 'readiness'])
          )}
        </>
      )}
    </div>
  );
});

export default ExportRenderer;
